import { useState, useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AppointmentService } from "../../../services/api/appointment.service";
import { ClinicService } from "../../../services/api/clinic.service";
import { useAuth } from "../../../context/AuthContext";
import { AppointmentListing } from "../../../services/api/types";
import SkeletonAppointmentTable from "./SkeletonAppointmentTable";
import avatarPlaceholder from "../../../assets/images/default_avatar.jpg";
import toast from "react-hot-toast";
import {
  Search,
  Calendar as CalendarIcon,
  RotateCcw,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Clock,
  Building2,
  Video,
  MapPin,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  XCircle,
  CalendarCheck,
  Phone,
  RefreshCw,
} from "lucide-react";

type TabKey = "upcoming" | "completed" | "cancelled" | "refunded" | "payment pending";

interface TabConfig {
  key: TabKey;
  label: string;
  apiStatus: string;
  color: string;
  activeBg: string;
  activeBorder: string;
  badgeBg: string;
  badgeText: string;
  icon: typeof CalendarCheck;
  emptyTitle: string;
  emptyDesc: string;
}

const TABS: TabConfig[] = [
  {
    key: "upcoming",
    label: "Upcoming",
    apiStatus: "upcoming",
    color: "text-emerald-600",
    activeBg: "bg-emerald-50 text-emerald-700 border-emerald-500",
    activeBorder: "#10B981",
    badgeBg: "bg-emerald-100",
    badgeText: "text-emerald-800",
    icon: CalendarCheck,
    emptyTitle: "No Upcoming Appointments",
    emptyDesc: "There are no booked or in-consultation appointments scheduled.",
  },
  {
    key: "completed",
    label: "Completed",
    apiStatus: "completed",
    color: "text-blue-600",
    activeBg: "bg-blue-50 text-blue-700 border-blue-500",
    activeBorder: "#3B82F6",
    badgeBg: "bg-blue-100",
    badgeText: "text-blue-800",
    icon: CheckCircle2,
    emptyTitle: "No Completed Appointments",
    emptyDesc: "No appointments have been marked as completed yet.",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    apiStatus: "cancelled",
    color: "text-rose-600",
    activeBg: "bg-rose-50 text-rose-700 border-rose-500",
    activeBorder: "#EF4444",
    badgeBg: "bg-rose-100",
    badgeText: "text-rose-800",
    icon: XCircle,
    emptyTitle: "No Cancelled Appointments",
    emptyDesc: "No appointment cancellations recorded.",
  },
  {
    key: "refunded",
    label: "Refunded",
    apiStatus: "refunded",
    color: "text-purple-600",
    activeBg: "bg-purple-50 text-purple-700 border-purple-500",
    activeBorder: "#8B5CF6",
    badgeBg: "bg-purple-100",
    badgeText: "text-purple-800",
    icon: RotateCcw,
    emptyTitle: "No Refunded Appointments",
    emptyDesc: "There are no appointments with refunded or refund-initiated payments.",
  },
  {
    key: "payment pending",
    label: "Payment Pending",
    apiStatus: "payment pending",
    color: "text-amber-600",
    activeBg: "bg-amber-50 text-amber-700 border-amber-500",
    activeBorder: "#F59E0B",
    badgeBg: "bg-amber-100",
    badgeText: "text-amber-800",
    icon: AlertCircle,
    emptyTitle: "No Payment Pending Appointments",
    emptyDesc: "All appointments are either paid in full, cancelled, or refunded.",
  },
];

interface PaginationState {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

const AppointmentsOverviewTable = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState<TabKey>("upcoming");
  const [appointments, setAppointments] = useState<AppointmentListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);

  // Filters State
  const [searchQuery, setSearchQuery] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [bookedBy, setBookedBy] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [clinicsList, setClinicsList] = useState<{ id: string; name: string }[]>([]);

  // Date Filter State
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeDatePreset, setActiveDatePreset] = useState<string>("");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Pagination State
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const isAdmin = user?.role?.toLowerCase() === "admin";

  // Fetch Clinics for Admin
  useEffect(() => {
    if (isAdmin) {
      ClinicService.getClinics({ page: 1, pageSize: 100, searchText: "" })
        .then((res) => {
          if (res?.success && res?.data?.dataList) {
            setClinicsList(
              res.data.dataList.map((c: any) => ({
                id: c.clinicId || c.id,
                name: c.name || c.clinicName || "Clinic",
              }))
            );
          }
        })
        .catch((err) => console.error("Failed to load clinics list:", err));
    }
  }, [isAdmin]);

  // Close date picker popup on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Appointments
  const fetchAppointments = async (
    targetPage = pagination.page,
    targetPageSize = pagination.pageSize,
    silent = false
  ) => {
    try {
      if (silent) {
        setIsRefetching(true);
      } else {
        setIsLoading(true);
      }

      const activeTabConfig = TABS.find((t) => t.key === activeTab) || TABS[0];
      const targetClinicId = isAdmin
        ? clinicId || undefined
        : user?.role === "clinic" || user?.role === "medicalcamp"
        ? user?.id
        : undefined;

      const response = await AppointmentService.getAppointmentListingV2({
        page: targetPage,
        limit: targetPageSize,
        pageSize: targetPageSize,
        status: activeTabConfig.apiStatus,
        clinicId: targetClinicId,
        searchText: searchQuery.trim() || undefined,
        appointmentType: appointmentType || undefined,
        bookedBy: bookedBy || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });

      if (response.success && response.data) {
        setAppointments(response.data.appointments || []);
        const p = response.data.pagination;
        if (p) {
          setPagination({
            page: p.page || 1,
            pageSize: p.pageSize || targetPageSize,
            totalCount: p.totalAppointments || 0,
            totalPages: p.totalPages || 1,
          });
        }
      } else {
        setAppointments([]);
        setPagination((prev) => ({
          ...prev,
          page: 1,
          totalCount: 0,
          totalPages: 1,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch appointments:", error);
      toast.error("Error loading appointments");
      setAppointments([]);
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  // Re-fetch when tab or primary filters change (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAppointments(1, pagination.pageSize);
    }, 250);

    return () => clearTimeout(timer);
  }, [
    activeTab,
    searchQuery,
    appointmentType,
    bookedBy,
    clinicId,
    startDate,
    endDate,
    user?.id,
  ]);

  // Handle Tab Change
  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle Page Change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.page) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      fetchAppointments(newPage, pagination.pageSize);
    }
  };

  // Handle Page Size Change
  const handlePageSizeChange = (newSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newSize, page: 1 }));
    fetchAppointments(1, newSize);
  };

  // Quick Date Presets
  const applyDatePreset = (preset: "today" | "yesterday" | "last7" | "month" | "clear") => {
    const now = new Date();
    const formatDate = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (preset === "clear") {
      setStartDate("");
      setEndDate("");
      setActiveDatePreset("");
      setIsDatePickerOpen(false);
      return;
    }

    if (preset === "today") {
      const todayStr = formatDate(now);
      setStartDate(todayStr);
      setEndDate(todayStr);
      setActiveDatePreset("today");
    } else if (preset === "yesterday") {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const yStr = formatDate(yesterday);
      setStartDate(yStr);
      setEndDate(yStr);
      setActiveDatePreset("yesterday");
    } else if (preset === "last7") {
      const past7 = new Date(now);
      past7.setDate(now.getDate() - 6);
      setStartDate(formatDate(past7));
      setEndDate(formatDate(now));
      setActiveDatePreset("last7");
    } else if (preset === "month") {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(formatDate(firstDay));
      setEndDate(formatDate(lastDay));
      setActiveDatePreset("month");
    }

    setIsDatePickerOpen(false);
  };

  // Reset all filters
  const resetAllFilters = () => {
    setSearchQuery("");
    setAppointmentType("");
    setBookedBy("");
    setClinicId("");
    setStartDate("");
    setEndDate("");
    setActiveDatePreset("");
  };

  const hasActiveFilters = Boolean(
    searchQuery || appointmentType || bookedBy || clinicId || startDate || endDate
  );

  // Calculate pagination display range
  const getPageRange = () => {
    const totalPages = pagination.totalPages;
    const currentPage = pagination.page;
    const maxButtons = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = startPage + maxButtons - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    return Array.from({ length: Math.max(1, endPage - startPage + 1) }, (_, i) => startPage + i);
  };

  const handleViewDetails = (appointment: AppointmentListing) => {
    const role = user?.role?.toLowerCase() || "clinic";
    navigate({ to: `/${role}/appointments/${appointment.id}` });
  };

  const currentTabConfig = TABS.find((t) => t.key === activeTab) || TABS[0];

  // Helper formatting for dates & times
  const formatAppointmentDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Tabs Header with Inline Refresh Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-gray-200 gap-2 pb-px">
        {/* 5 Status Tabs */}
        <div className="flex space-x-1 sm:space-x-3 overflow-x-auto scrollbar-none">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const Icon = tab.icon;

            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`group flex items-center gap-1.5 py-2.5 px-3 sm:px-3.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-all duration-150 rounded-t-md ${
                  isActive
                    ? `${tab.color} border-current bg-white shadow-xs`
                    : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50/50"
                }`}
              >
                <Icon
                  className={`w-4 h-4 transition-colors ${
                    isActive ? tab.color : "text-gray-400 group-hover:text-gray-600"
                  }`}
                />
                <span>{tab.label}</span>
                {isActive && pagination.totalCount > 0 && (
                  <span
                    className={`ml-1 px-2 py-0.5 text-xs font-bold rounded-full ${tab.badgeBg} ${tab.badgeText}`}
                  >
                    {pagination.totalCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Refresh Action */}
        <div className="flex items-center justify-end pb-2 sm:pb-0">
          <button
            onClick={() => fetchAppointments(pagination.page, pagination.pageSize, true)}
            title="Refresh appointments list"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 shadow-2xs transition-all duration-150"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${
                isRefetching ? "animate-spin text-emerald-600" : "text-gray-500"
              }`}
            />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs p-3 space-y-2.5">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[240px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by Patient Name, Mobile, or DHR ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-9 pr-8 py-1.5 border border-gray-300 rounded-lg text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Date Range Picker Popover */}
            <div className="relative" ref={datePickerRef}>
              <button
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-all ${
                  startDate || endDate
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800 shadow-2xs"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <CalendarIcon className="w-3.5 h-3.5 text-gray-500" />
                <span>
                  {startDate && endDate
                    ? `${startDate} ~ ${endDate}`
                    : startDate
                    ? `From ${startDate}`
                    : endDate
                    ? `Until ${endDate}`
                    : "Filter Date"}
                </span>
                {(startDate || endDate) && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      applyDatePreset("clear");
                    }}
                    className="hover:text-rose-600 ml-0.5"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </button>

              {isDatePickerOpen && (
                <div className="absolute right-0 mt-1.5 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-3 space-y-3">
                  <div className="flex items-center justify-between border-b pb-1.5">
                    <span className="text-[11px] font-semibold uppercase text-gray-500 tracking-wider">
                      Select Date Range
                    </span>
                    <button
                      onClick={() => applyDatePreset("clear")}
                      className="text-xs text-rose-600 hover:underline font-medium"
                    >
                      Clear
                    </button>
                  </div>

                  {/* Preset Pills */}
                  <div className="grid grid-cols-2 gap-1">
                    <button
                      onClick={() => applyDatePreset("today")}
                      className={`px-2 py-1 text-xs font-medium rounded-md border transition-all ${
                        activeDatePreset === "today"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => applyDatePreset("yesterday")}
                      className={`px-2 py-1 text-xs font-medium rounded-md border transition-all ${
                        activeDatePreset === "yesterday"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      Yesterday
                    </button>
                    <button
                      onClick={() => applyDatePreset("last7")}
                      className={`px-2 py-1 text-xs font-medium rounded-md border transition-all ${
                        activeDatePreset === "last7"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      Last 7 Days
                    </button>
                    <button
                      onClick={() => applyDatePreset("month")}
                      className={`px-2 py-1 text-xs font-medium rounded-md border transition-all ${
                        activeDatePreset === "month"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      This Month
                    </button>
                  </div>

                  {/* Custom Dates */}
                  <div className="space-y-1.5 pt-1.5 border-t">
                    <div>
                      <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setActiveDatePreset("custom");
                        }}
                        className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setActiveDatePreset("custom");
                        }}
                        className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="pt-1 flex justify-end">
                    <button
                      onClick={() => setIsDatePickerOpen(false)}
                      className="px-2.5 py-1 text-xs font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700 shadow-2xs"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Appointment Consultation Type */}
            <select
              value={appointmentType}
              onChange={(e) => setAppointmentType(e.target.value)}
              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all cursor-pointer"
            >
              <option value="">All Types</option>
              <option value="Offline">Offline (In-Clinic)</option>
              <option value="Online">Online (Video)</option>
              <option value="IA">IA Consultation</option>
            </select>

            {/* Booked By Channel */}
            <select
              value={bookedBy}
              onChange={(e) => setBookedBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all cursor-pointer"
            >
              <option value="">All Channels</option>
              <option value="clinic">Clinic</option>
              <option value="patient">Patient App/Web</option>
              <option value="patient-whatsapp">WhatsApp</option>
              <option value="admin">Admin</option>
            </select>

            {/* Clinic Filter for Admin */}
            {isAdmin && clinicsList.length > 0 && (
              <select
                value={clinicId}
                onChange={(e) => setClinicId(e.target.value)}
                className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-all cursor-pointer max-w-[170px] truncate"
              >
                <option value="">All Clinics</option>
                {clinicsList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}

            {/* Clear All Filters Button */}
            {hasActiveFilters && (
              <button
                onClick={resetAllFilters}
                className="flex items-center gap-1 px-2 py-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                title="Clear all filters"
              >
                <X className="w-3 h-3" />
                <span>Reset</span>
              </button>
            )}
          </div>
        </div>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1.5 border-t border-gray-100 text-[11px]">
            <span className="text-gray-400 font-medium">Active filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                Search: "{searchQuery}"
                <X
                  className="w-3 h-3 cursor-pointer hover:text-rose-600"
                  onClick={() => setSearchQuery("")}
                />
              </span>
            )}
            {(startDate || endDate) && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                Date: {startDate || "Any"} ~ {endDate || "Any"}
                <X
                  className="w-3 h-3 cursor-pointer hover:text-rose-600"
                  onClick={() => applyDatePreset("clear")}
                />
              </span>
            )}
            {appointmentType && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-medium">
                Type: {appointmentType}
                <X
                  className="w-3 h-3 cursor-pointer hover:text-rose-600"
                  onClick={() => setAppointmentType("")}
                />
              </span>
            )}
            {bookedBy && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                Channel: {bookedBy}
                <X
                  className="w-3 h-3 cursor-pointer hover:text-rose-600"
                  onClick={() => setBookedBy("")}
                />
              </span>
            )}
            {clinicId && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                Clinic: {clinicsList.find((c) => c.id === clinicId)?.name || clinicId}
                <X
                  className="w-3 h-3 cursor-pointer hover:text-rose-600"
                  onClick={() => setClinicId("")}
                />
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main Table View / Skeletons / Empty State */}
      {isLoading ? (
        <SkeletonAppointmentTable />
      ) : appointments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 px-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
          <div className="text-center max-w-md">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3.5 ${currentTabConfig.badgeBg}`}
            >
              <currentTabConfig.icon className={`h-7 w-7 ${currentTabConfig.color}`} />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {currentTabConfig.emptyTitle}
            </h3>
            <p className="text-gray-500 text-xs sm:text-sm mb-4">
              {hasActiveFilters
                ? "No matching appointments found with the selected filters. Try broadening your search or date range."
                : currentTabConfig.emptyDesc}
            </p>
            {hasActiveFilters && (
              <button
                onClick={resetAllFilters}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-lg hover:bg-emerald-100 transition-colors shadow-2xs"
              >
                <X className="w-3.5 h-3.5" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-2xs bg-white">
            <table className="min-w-full divide-y divide-gray-200 table-auto">
              <thead className="bg-gray-50/80">
                <tr>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    S.No.
                  </th>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Patient Details
                  </th>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Doctor & Clinic
                  </th>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Origin
                  </th>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3.5 py-3 text-left font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Payment Details
                  </th>
                  <th className="px-3.5 py-3 text-center font-semibold text-xs text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {appointments.map((appt, index) => {
                  const serialNumber = (pagination.page - 1) * pagination.pageSize + index + 1;

                  return (
                    <tr
                      key={appt.id || index}
                      className="hover:bg-gray-50/70 transition-colors group"
                    >
                      {/* S.No */}
                      <td className="px-3.5 py-3 whitespace-nowrap text-xs font-semibold text-gray-400">
                        #{serialNumber}
                      </td>

                      {/* Patient Details */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2.5">
                          <div className="flex-shrink-0 h-8 w-8">
                            <img
                              className="h-8 w-8 rounded-full object-cover border border-gray-200 shadow-2xs"
                              src={avatarPlaceholder}
                              alt={appt.patientName || "Patient"}
                            />
                          </div>
                          <div>
                            <div className="text-xs sm:text-sm font-semibold text-gray-900 group-hover:text-emerald-700 transition-colors">
                              {appt.patientName || "Unknown Patient"}
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                              {appt.patientNumber && (
                                <span className="flex items-center gap-0.5">
                                  <Phone className="w-2.5 h-2.5 text-gray-400" />
                                  {appt.patientNumber}
                                </span>
                              )}
                              {appt.dhrId && (
                                <span className="inline-block px-1 py-0.2 rounded bg-gray-100 text-gray-600 font-mono text-[10px]">
                                  {appt.dhrId}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Schedule */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <div className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                          <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                          {formatAppointmentDate(appt.appointmentDate)}
                        </div>
                        {appt.appointmentTime && (
                          <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3 text-gray-400" />
                            {appt.appointmentTime}
                          </div>
                        )}
                      </td>

                      {/* Doctor & Clinic */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <div className="text-xs sm:text-sm font-medium text-gray-900">
                          {appt.doctorName || "Assigned Doctor"}
                        </div>
                        <div className="text-[11px] text-gray-500 flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-gray-400" />
                          <span className="truncate max-w-[150px]" title={appt.clinicName}>
                            {appt.clinicName || "Clinic"}
                          </span>
                        </div>
                      </td>

                      {/* Appointment Type */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        {(() => {
                          const type = (appt.appointmentType || "").toLowerCase();
                          if (type === "online") {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                <Video className="w-3 h-3" />
                                Online
                              </span>
                            );
                          }
                          if (type === "ia") {
                            return (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                                <Sparkles className="w-3 h-3" />
                                IA
                              </span>
                            );
                          }
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              <MapPin className="w-3 h-3" />
                              Offline
                            </span>
                          );
                        })()}
                      </td>

                      {/* Origin (Booked By) */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        {(() => {
                          const source = (appt.bookedBy || "").toLowerCase();
                          if (source === "patient-whatsapp" || source === "whatsapp") {
                            return (
                              <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                                WhatsApp
                              </span>
                            );
                          }
                          if (source === "patient") {
                            return (
                              <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200">
                                Patient App
                              </span>
                            );
                          }
                          if (source === "admin") {
                            return (
                              <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-md bg-purple-50 text-purple-700 border border-purple-200">
                                Admin
                              </span>
                            );
                          }
                          return (
                            <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-md bg-gray-50 text-gray-700 border border-gray-200">
                              Clinic
                            </span>
                          );
                        })()}
                      </td>

                      {/* Status / Cancellation Reason */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <div className="space-y-0.5">
                          {(() => {
                            const status = (appt.currentStatus || "").toLowerCase();
                            if (status === "booked") {
                              return (
                                <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                                  Booked
                                </span>
                              );
                            }
                            if (status === "in consultation" || status === "in_consultation") {
                              return (
                                <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-teal-100 text-teal-800">
                                  In Consultation
                                </span>
                              );
                            }
                            if (status === "completed") {
                              return (
                                <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                  Completed
                                </span>
                              );
                            }
                            if (status === "cancelled") {
                              return (
                                <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-rose-100 text-rose-800">
                                  Cancelled
                                </span>
                              );
                            }
                            if (
                              status === "pending payment" ||
                              status === "payment pending" ||
                              status === "payment failed"
                            ) {
                              return (
                                <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                  Payment Pending
                                </span>
                              );
                            }
                            return (
                              <span className="px-2 py-0.5 inline-flex text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                {appt.currentStatus || "Scheduled"}
                              </span>
                            );
                          })()}

                          {appt.cancellationReason && (
                            <div
                              className="text-[10px] text-rose-600 font-medium max-w-[130px] truncate"
                              title={appt.cancellationReason}
                            >
                              Reason: {appt.cancellationReason}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Payment Breakdown */}
                      <td className="px-3.5 py-3 whitespace-nowrap">
                        <div className="text-xs font-semibold text-gray-900">
                          Total: ₹{appt.totalAmount ?? 0}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] mt-0.5">
                          <span className="text-gray-500">Paid: ₹{appt.amountPaid ?? 0}</span>
                          {(appt.balanceDue ?? 0) > 0 ? (
                            <span className="font-semibold text-amber-600 bg-amber-50 px-1 py-0.2 rounded text-[10px]">
                              Due: ₹{appt.balanceDue}
                            </span>
                          ) : (
                            <span className="text-emerald-600 font-medium text-[10px]">Cleared</span>
                          )}
                        </div>
                        <div className="mt-0.5">
                          {(() => {
                            const pStatus = (appt.paymentStatus || "").toLowerCase();
                            if (pStatus === "completed" || pStatus === "paid") {
                              return (
                                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded">
                                  Paid ({appt.paymentType || "N/A"})
                                </span>
                              );
                            }
                            if (pStatus === "partially paid" || pStatus === "partially_paid") {
                              return (
                                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded">
                                  Partial ({appt.paymentType || "N/A"})
                                </span>
                              );
                            }
                            if (pStatus === "refunded" || pStatus === "refund_initiated") {
                              return (
                                <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.2 rounded">
                                  Refunded
                                </span>
                              );
                            }
                            return (
                              <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.2 rounded">
                                {appt.paymentStatus || "Unpaid"}
                              </span>
                            );
                          })()}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-3.5 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleViewDetails(appt)}
                          className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors shadow-none"
                          title="View Appointment Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 py-2.5 bg-white border border-gray-200 rounded-xl shadow-2xs">
            {/* Rows Per Page */}
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Showing</span>
              <span className="font-semibold text-gray-900">
                {(pagination.page - 1) * pagination.pageSize + 1}
              </span>
              <span>to</span>
              <span className="font-semibold text-gray-900">
                {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}
              </span>
              <span>of</span>
              <span className="font-semibold text-gray-900">{pagination.totalCount}</span>
              <span>appointments</span>

              <span className="mx-1 text-gray-300">|</span>

              <span>Rows:</span>
              <select
                value={pagination.pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-1.5 py-0.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                {[10, 20, 30, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Page Navigation */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                className="p-1 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                title="First Page"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-1 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                title="Previous Page"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              {getPageRange().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-6 h-6 flex items-center justify-center text-xs font-semibold rounded-md transition-colors ${
                    pagination.page === page
                      ? "bg-emerald-600 text-white shadow-2xs"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="p-1 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                title="Next Page"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages}
                className="p-1 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-gray-600"
                title="Last Page"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsOverviewTable;
